/**
 * Afrikaans: Suggest flows with AI
 *
 * What this file covers: Suggest flows with AI: the page where a business gives its ideas, the AI suggests flows from its knowledge base, its Insights report and its flows, asks questions in a chat, and builds only the picked ones (switched off); its settings, models and credits, the AI's live activity and memory.
 *
 * Tone: Clear and encouraging, for business owners planning their chatbot. Say what the AI is doing in plain words; be exact about credits and that nothing is built or switched on without the person.
 *
 * Afrikaans: Standard South African Afrikaans. Address the reader as "jy". Use the same Afrikaans word for the
 * same English term everywhere; menu.ts and common.ts set the words the other files follow. Afrikaans must stay
 * complete: the release checks fail if a key is missing.
 *
 * Rules for every translation: keep each key exactly as it is and translate only the text after it. Keep
 * {{placeholders}} such as {{count}} or {{name}} unchanged, and keep product names (RepletoryReach, WhatsApp,
 * Meta, Google, Outlook) as they are. Never use the em dash.
 */

import type { Catalog } from '../types';

const flowIdeas: Catalog = {
  'flowIdeas.title': 'Stel vloeie voor met KI',
  'flowIdeas.description': 'Sê vir die KI wat jy in gedagte het, of niks nie. Dit stel vloeie voor uit jou kennisbasis, wat jou kliënte gevra het en die vloeie wat jy reeds het, vra wanneer iets onduidelik is, en bou net dié wat jy kies.',
  'flowIdeas.backToFlows': 'Terug na Vloeie',
  'flowIdeas.tabs.flows': 'Jou vloeie',
  'flowIdeas.tabs.ai': 'KI',
  'flowIdeas.tabs.attention': 'Die KI werk of het jou iets gevra',
  'flowIdeas.start.title': 'Wat moet jou kletsbot doen?',
  'flowIdeas.start.intro': 'Begin hier. Die KI werk aan die regterkant en wys wat dit doen; sy voorstelle kom hier terug sodat jy kan kies.',
  'flowIdeas.start.ideas': 'Jou idees (opsioneel)',
  'flowIdeas.start.ideasHint': 'Tik of plak enigiets: ’n lys, ’n e-pos, hoe jy navrae nou hanteer. Laat dit leeg en die KI stel vloeie voor uit jou kennisbasis en wat jou kliënte gevra het.',
  'flowIdeas.start.ideasPlaceholder': 'Byvoorbeeld: ouers vra oor fooie vir elke graad, skoolvervoer en opedae. Ons wil skooltoere bespreek en aansoeke aanneem.',
  'flowIdeas.start.settings': 'Instellings',
  'flowIdeas.start.change': 'Verander instellings',
  'flowIdeas.start.suggest': 'Stel vloeie voor',
  'flowIdeas.start.suggestFromKb': 'Stel voor uit my kennisbasis',
  'flowIdeas.start.price': 'Sowat {{credits}} vir die voorstelle. Neem omtrent ’n minuut.',
  'flowIdeas.start.priceUnknown': 'Die prys word uitgewerk wanneer dit begin. Neem omtrent ’n minuut.',
  'flowIdeas.summary.knowledge': 'Kennisbasis',
  'flowIdeas.summary.documents': 'Gekose dokumente: {{count}}',
  'flowIdeas.summary.insights': 'Insigte van {{date}}',
  'flowIdeas.summary.count': '{{count}} voorstelle',
  'flowIdeas.summary.safetyOn': 'Veiligheid aan',
  'flowIdeas.summary.safetySome': 'Veiligheid deels aan',
  'flowIdeas.summary.safetyOff': 'Veiligheid af',
  'flowIdeas.settings.title': 'Instellings',
  'flowIdeas.settings.uses.title': 'Wat die KI gebruik',
  'flowIdeas.settings.uses.knowledge': 'Jou kennisbasis',
  'flowIdeas.settings.uses.knowledgeHint': 'Jou dokumente en regstellings ({{count}} dokumente), sodat die vloeie antwoord uit wat jy geskryf het.',
  'flowIdeas.settings.uses.allDocuments': 'Elke dokument',
  'flowIdeas.settings.uses.allDocumentsHint': 'Skakel af om die dokumente te kies wat die KI lees.',
  'flowIdeas.settings.uses.documents': 'Dokumente om te gebruik',
  'flowIdeas.settings.uses.insights': 'Wat kliënte gevra het',
  'flowIdeas.settings.uses.insightsHint': 'Jou insigverslag van {{date}}: die vrae wat niemand beantwoord het nie, die toponderwerpe en waar gesprekke gestop het.',
  'flowIdeas.settings.uses.flows': 'Jou bestaande vloeie ({{count}})',
  'flowIdeas.settings.uses.flowsHint': 'Sodat dit nooit een herhaal nie, verbeterings daaraan voorstel en nuwe vloeie daaraan koppel.',
  'flowIdeas.settings.uses.apis': 'Jou API’s en lewendige bronne ({{count}})',
  'flowIdeas.settings.uses.apisHint': 'Sodat ’n vloei dinge kan naslaan of besonderhede na jou stelsels kan stuur.',
  'flowIdeas.settings.uses.bookings': 'Besprekings',
  'flowIdeas.settings.uses.bookingsHint': 'Vloeie mag jou dienste en klasse bespreek.',
  'flowIdeas.settings.uses.tickets': 'Kaartjies',
  'flowIdeas.settings.uses.ticketsHint': 'Vloeie mag kaartjies vir jou span oopmaak.',
  'flowIdeas.settings.goal.title': 'Doel en besonderhede',
  'flowIdeas.settings.goal.goals': 'Waarvoor die vloeie is',
  'flowIdeas.settings.goal.goalsHint': 'Kies soveel as wat jy wil, of geen sodat die KI uit jou besigheid besluit.',
  'flowIdeas.settings.goal.count': 'Hoeveel voorstelle',
  'flowIdeas.settings.goal.countHint': '{{min}} tot {{max}}. Jy kan later in die klets vir meer vra.',
  'flowIdeas.settings.goal.detail': 'Besonderhede',
  'flowIdeas.settings.voice.title': 'Kanale, taal en toon',
  'flowIdeas.settings.voice.channels': 'Kanale',
  'flowIdeas.settings.voice.channelsHint': 'Waar die vloeie sal loop. Elke kanaal het sy eie perke op knoppies en lyste.',
  'flowIdeas.settings.voice.language': 'Taal wat die vloeie praat',
  'flowIdeas.settings.voice.languageHint': 'Die taal waarin kliënte die boodskappe kry. Van jou kletsbot geneem.',
  'flowIdeas.settings.voice.tone': 'Toon',
  'flowIdeas.settings.voice.toneHint': 'Van jou kletsbot se persona geneem.',
  'flowIdeas.settings.voice.bot': 'Kletsbot',
  'flowIdeas.settings.voice.botHint': 'Die kletsbot waaraan die nuwe vloeie behoort.',
  'flowIdeas.settings.voice.botDefault': '{{name}} (hoof)',
  'flowIdeas.settings.safety.title': 'Veiligheid en oorgee',
  'flowIdeas.settings.safety.person': 'Elke vloei bied ’n mens aan',
  'flowIdeas.settings.safety.personHint': '’n Kliënt kan altyd vra om met iemand in jou span te praat.',
  'flowIdeas.settings.safety.hours': 'Respekteer kantoorure',
  'flowIdeas.settings.safety.hoursHint': 'Buite jou ure sê vloeie wanneer iemand sal antwoord, eerder as om nou ’n antwoord te belowe.',
  'flowIdeas.settings.safety.consent': 'Vra voordat besonderhede ingesamel word',
  'flowIdeas.settings.safety.consentHint': 'Vloeie vra die kliënt se toestemming voordat hulle ’n naam, nommer of e-pos hou (POPIA).',
  'flowIdeas.settings.models.title': 'Modelle',
  'flowIdeas.settings.models.suggest': 'Model vir voorstelle',
  'flowIdeas.settings.models.suggestPrice': 'Sowat {{credits}} per boodskap aan die KI.',
  'flowIdeas.settings.models.build': 'Model om vloeie te bou',
  'flowIdeas.settings.models.buildPrice': 'Sowat {{credits}} per vloei.',
  'flowIdeas.settings.models.note': '’n Sterker model dink deegliker en gebruik jou krediete vinniger. Voorstelle is een boodskap op ’n slag, so ’n sterk model kos min daar.',
  'flowIdeas.settings.modelStandard': '{{name}} (standaard)',
  'flowIdeas.settings.priceUnknown': 'Die prys word uitgewerk wanneer dit gebruik word.',
  'flowIdeas.settings.done': 'Klaar',
  'flowIdeas.settings.save': 'Stoor instellings',
  'flowIdeas.settings.saved': 'Instellings gestoor. Die KI gebruik dit vanaf sy volgende boodskap.',
  'flowIdeas.goal.answer_questions': 'Beantwoord vrae',
  'flowIdeas.goal.capture_leads': 'Vang leidrade',
  'flowIdeas.goal.bookings': 'Besprekings',
  'flowIdeas.goal.support': 'Ondersteuning en klagtes',
  'flowIdeas.goal.sales': 'Verkope en bestellings',
  'flowIdeas.goal.enrolment': 'Inskrywing en aansoeke',
  'flowIdeas.goal.after_hours': 'Na-ure',
  'flowIdeas.goal.feedback': 'Terugvoer en opnames',
  'flowIdeas.detail.simple.name': 'Eenvoudig',
  'flowIdeas.detail.simple.hint': '3 tot 6 stappe: vinnig om na te gaan en maklik om te verander.',
  'flowIdeas.detail.complete.name': 'Volledig',
  'flowIdeas.detail.complete.hint': 'Vorms, vertakkings en bevestigings, gereed vir regte kliënte.',
  'flowIdeas.channel.whatsapp': 'WhatsApp',
  'flowIdeas.channel.web': 'Webwerf-klets',
  'flowIdeas.channel.messenger': 'Messenger',
  'flowIdeas.channel.instagram': 'Instagram',
  'flowIdeas.tone.friendly': 'Vriendelik',
  'flowIdeas.tone.formal': 'Formeel',
  'flowIdeas.tone.playful': 'Speels',
  'flowIdeas.lang.en': 'Engels',
  'flowIdeas.lang.af': 'Afrikaans',
  'flowIdeas.lang.zu': 'isiZulu',
  'flowIdeas.lang.xh': 'isiXhosa',
  'flowIdeas.lang.st': 'Sesotho',
  'flowIdeas.lang.tn': 'Setswana',
  'flowIdeas.lang.nso': 'Sepedi',
  'flowIdeas.lang.pt': 'Portugees',
  'flowIdeas.lang.fr': 'Frans',
  'flowIdeas.priority.high': 'Hoë voorrang',
  'flowIdeas.priority.medium': 'Middelmatige voorrang',
  'flowIdeas.priority.low': 'Lae voorrang',
  'flowIdeas.need.document': 'Dokument',
  'flowIdeas.need.template': 'WhatsApp-sjabloon',
  'flowIdeas.need.number': 'Nommer',
  'flowIdeas.need.api': 'API',
  'flowIdeas.need.info': 'Inligting',
  'flowIdeas.source.document': '’n Dokument in jou kennisbasis',
  'flowIdeas.source.correction': '’n Regstelling wat jy geskryf het',
  'flowIdeas.source.flow': 'Een van jou vloeie',
  'flowIdeas.source.insight': 'Jou insigverslag',
  'flowIdeas.source.idea': 'Jou idees',
  'flowIdeas.source.api': 'Een van jou API’s',
  'flowIdeas.status.idle': 'Wag vir jou',
  'flowIdeas.status.thinking': 'Die KI dink',
  'flowIdeas.status.building': 'Bou jou vloeie',
  'flowIdeas.status.done': 'Klaar',
  'flowIdeas.status.failed': 'Iets het verkeerd geloop',
  'flowIdeas.status.cancelled': 'Gestop',
  'flowIdeas.itemStatus.proposed': 'Voorgestel',
  'flowIdeas.itemStatus.removed': 'Verwyder',
  'flowIdeas.itemStatus.queued': 'Wag',
  'flowIdeas.itemStatus.building': 'Word gebou',
  'flowIdeas.itemStatus.created': 'Geskep',
  'flowIdeas.itemStatus.failed': 'Nie gebou nie',
  'flowIdeas.itemStatus.skipped': 'Nie geskep nie',
  'flowIdeas.set.title': 'Jou voorstelle',
  'flowIdeas.set.cancel': 'Stop',
  'flowIdeas.set.new': 'Begin weer',
  'flowIdeas.set.waiting': 'Die KI lees en skryf. Kyk regs wat dit doen; sy voorstelle verskyn hier.',
  'flowIdeas.set.none': 'Nog geen voorstelle nie',
  'flowIdeas.set.noneHint': 'Beantwoord die KI se vrae of vertel dit meer in die klets, en sy voorstelle verskyn hier.',
  'flowIdeas.cards.title': 'Voorgestelde vloeie: {{count}}',
  'flowIdeas.cards.hint': 'Merk dié wat jy wil hê. Niks word gebou voordat jy Bou druk nie, en elke vloei begin afgeskakel.',
  'flowIdeas.cards.goal': 'Doel',
  'flowIdeas.cards.anyGoal': 'Enige doel',
  'flowIdeas.cards.priority': 'Voorrang',
  'flowIdeas.cards.anyPriority': 'Enige voorrang',
  'flowIdeas.cards.filteredOut': 'Geen voorstel pas by hierdie filters nie.',
  'flowIdeas.card.number': 'Voorstel {{n}}',
  'flowIdeas.card.new': 'Nuwe vloei',
  'flowIdeas.card.improve': 'Verbetering',
  'flowIdeas.card.improves': 'Verbeter jou vloei',
  'flowIdeas.card.pick': 'Bou {{name}}',
  'flowIdeas.card.purpose': 'Doel',
  'flowIdeas.card.outcome': 'Uitkoms',
  'flowIdeas.card.starts': 'Wanneer dit begin',
  'flowIdeas.card.steps': 'Stappe',
  'flowIdeas.card.stepsNow': 'Stappe nou',
  'flowIdeas.card.stepsAfter': 'Stappe daarna',
  'flowIdeas.card.why': 'Waarom',
  'flowIdeas.card.sources': 'Waarop dit gebaseer is',
  'flowIdeas.card.uses': 'Gebruik',
  'flowIdeas.card.usesFlow': 'Loop jou vloei {{name}}',
  'flowIdeas.card.usesBookings': 'Besprekings',
  'flowIdeas.card.usesTickets': 'Kaartjies',
  'flowIdeas.card.needs': 'Benodig van jou',
  'flowIdeas.card.check': 'Gaan na voor jy bou',
  'flowIdeas.card.price': 'Sowat {{credits}} om te bou',
  'flowIdeas.card.priceUnknown': 'Prys word uitgewerk wanneer dit gebou word',
  'flowIdeas.card.ask': 'Vra die KI om dit te verander',
  'flowIdeas.card.askPrefill': 'Oor “{{name}}”: ',
  'flowIdeas.starts.keywords': 'Wanneer ’n kliënt {{keywords}} skryf',
  'flowIdeas.starts.intent': 'Wanneer ’n kliënt wil {{intent}}',
  'flowIdeas.starts.entry': 'As jou {{entry}}-vloei',
  'flowIdeas.starts.unknown': 'Nog nie gestel nie',
  'flowIdeas.flag.notGrounded': 'Nie gegrond nie: niks in jou kennisbasis, vloeie of idees ondersteun dit nie. Gaan dit na voor jy bou.',
  'flowIdeas.flag.factNotFound': '“{{detail}}” is nie in jou dokumente nie. Die vloei moet dit eerder uit die kennisbasis beantwoord.',
  'flowIdeas.flag.unknownDocument': 'Die dokument “{{detail}}” is nie in jou kennisbasis gevind nie.',
  'flowIdeas.flag.unknownApi': 'Die API “{{detail}}” is nie gevind nie.',
  'flowIdeas.flag.unknownFlow': 'Die vloei “{{detail}}” is nie gevind nie.',
  'flowIdeas.flag.keywordClash': 'Die sleutelwoord “{{detail}}” word reeds deur {{flow}} gebruik.',
  'flowIdeas.flag.keywordClashAny': 'Die sleutelwoord “{{detail}}” word reeds deur ’n ander vloei gebruik.',
  'flowIdeas.flag.overlaps': 'Naby aan jou vloei {{flow}}. Om daardie vloei te verbeter is dalk beter.',
  'flowIdeas.flag.overlapsAny': 'Naby aan een van jou vloeie.',
  'flowIdeas.flag.entryTaken': '{{flow}} hanteer dit reeds.',
  'flowIdeas.flag.entryTakenAny': '’n Ander vloei hanteer dit reeds.',
  'flowIdeas.build.picked': 'Gekies: {{count}}',
  'flowIdeas.build.pickSome': 'Merk die voorstelle wat jy gebou wil hê.',
  'flowIdeas.build.total': 'Sowat {{credits}} om hulle te bou',
  'flowIdeas.build.totalUnknown': 'Die prys word uitgewerk soos hulle gebou word.',
  'flowIdeas.build.room': 'Jou plan laat nog {{count}} vloeie toe.',
  'flowIdeas.build.roomUnlimited': 'Jou plan het geen perk op vloeie nie.',
  'flowIdeas.build.model': 'Model om te bou',
  'flowIdeas.build.button': 'Bou {{count}}',
  'flowIdeas.build.switchedOff': 'Afgeskakel geskep: niks bereik kliënte voordat jy ’n vloei aanskakel nie.',
  'flowIdeas.build.started': 'Die bou het begin. Jy kan hierdie bladsy verlaat: ons e-pos jou en stuur ’n kennisgewing wanneer hulle gereed is.',
  'flowIdeas.confirm.title': 'Bou hierdie vloeie ({{count}})?',
  'flowIdeas.confirm.model': 'Gebou met {{model}}.',
  'flowIdeas.confirm.price': 'Sowat {{credits}} altesaam.',
  'flowIdeas.confirm.off': 'Elke vloei word afgeskakel geskep. Maak elkeen in die bouer oop, toets dit, en skakel dit dan aan.',
  'flowIdeas.confirm.improve': '’n Verbetering word as ’n kopie van jou vloei gebou. Jou vloei bly soos dit is totdat jy dit met die kopie vervang.',
  'flowIdeas.confirm.room': 'Jou plan het plek vir {{fit}} van hierdie {{count}}. Die res sal nie geskep word nie.',
  'flowIdeas.confirm.leave': 'Bou neem sowat ’n minuut per vloei. Jy kan hierdie bladsy verlaat: jy kry ’n e-pos en ’n kennisgewing wanneer hulle gereed is.',
  'flowIdeas.results.title': 'Jou nuwe vloeie',
  'flowIdeas.results.building': 'Bou jou vloeie',
  'flowIdeas.results.count': '{{done}} van {{total}} klaar, {{created}} geskep',
  'flowIdeas.results.leave': 'Jy kan hierdie bladsy verlaat. Jy kry ’n e-pos en ’n kennisgewing wanneer hulle gereed is.',
  'flowIdeas.results.off': 'Afgeskakel',
  'flowIdeas.results.open': 'Maak oop in bouer',
  'flowIdeas.results.problems': 'Gaan na in die bouer',
  'flowIdeas.results.needs': 'Benodig nog',
  'flowIdeas.results.clashes': 'Sleutelwoordbotsings',
  'flowIdeas.results.switch': 'Skakel oor na die verbeterde een',
  'flowIdeas.results.switchTitle': 'Skakel oor na die verbeterde vloei?',
  'flowIdeas.results.switchBody': '{{name}} word aangeskakel en {{original}} afgeskakel. Albei word gehou, so jy kan enige tyd terugskakel.',
  'flowIdeas.results.switched': 'Nou in gebruik',
  'flowIdeas.results.switchedToast': 'Die verbeterde vloei is nou aangeskakel. Die oorspronklike word afgeskakel gehou.',
  'flowIdeas.ai.title': 'KI',
  'flowIdeas.ai.idle': 'Wanneer jy begin, wys ek hier wat ek doen: jou kennisbasis lees, jou vloeie nagaan en voorstelle skryf. As iets onduidelik is, vra ek jou hier, en jy kan my vra om enigiets te verander.',
  'flowIdeas.ai.thinking': 'Dink...',
  'flowIdeas.ai.placeholder': 'Vra vir veranderings, meer voorstelle, of om sommige te bou',
  'flowIdeas.ai.placeholderAnswer': 'Antwoord, of tik op een van die knoppies hierbo',
  'flowIdeas.ai.send': 'Stuur',
  'flowIdeas.ai.wait': 'Wag dat die KI klaarmaak.',
  'flowIdeas.ai.estimate': 'Hierdie boodskap: sowat {{in}} tokens in, {{out}} uit, sowat {{credits}}.',
  'flowIdeas.ai.estimateNone': 'Tik ’n boodskap om te sien wat dit kos.',
  'flowIdeas.ai.wontFit': 'Dit sal te veel wees vir die KI om by te hou. Begin ’n nuwe stel om voort te gaan.',
  'flowIdeas.ai.used': '{{tokens}} tokens gebruik, {{credits}}',
  'flowIdeas.ai.spent': 'Hierdie stel: {{credits}}, {{tokens}} tokens',
  'flowIdeas.activity.title': 'Wat die KI doen',
  'flowIdeas.activity.knowledge': 'Lees jou kennisbasis ({{count}} dokumente)',
  'flowIdeas.activity.insights': 'Kyk wat kliënte gevra het (Insigte van {{date}})',
  'flowIdeas.activity.flows': 'Gaan jou {{count}} vloeie na',
  'flowIdeas.activity.apis': 'Kyk na jou API’s ({{count}})',
  'flowIdeas.activity.writing': 'Skryf voorstelle',
  'flowIdeas.activity.writingRange': 'Skryf voorstelle {{from}} tot {{to}} van {{total}}',
  'flowIdeas.activity.folding': 'Som ons vroeëre gesprek op',
  'flowIdeas.activity.building': 'Bou “{{name}}” ({{n}} van {{total}})',
  'flowIdeas.activity.checking': 'Gaan die vloei na',
  'flowIdeas.activity.checkingNamed': 'Gaan “{{name}}” na',
  'flowIdeas.memory.label': 'Geheue',
  'flowIdeas.memory.used': '{{used}} van {{budget}} tokens',
  'flowIdeas.memory.green': 'Baie plek: die KI hou alles by.',
  'flowIdeas.memory.amber': 'Raak vol: ouer klets sal opgesom word sodat die KI akkuraat bly.',
  'flowIdeas.memory.red': 'Amper vol: die KI het dalk binnekort ’n nuwe stel nodig om akkuraat te bly.',
  'flowIdeas.memory.folded': 'Ek het ons vroeëre gesprek opgesom om akkuraat te bly.',
  'flowIdeas.memory.showNote': 'Wys die opsomming',
  'flowIdeas.memory.hideNote': 'Versteek die opsomming',
  'flowIdeas.memory.tooBig': 'Hierdie gesprek het te groot geword vir die KI om akkuraat te bly. Begin ’n nuwe stel: jou instellings, die voorstelle wat jy gehou het en ’n opsomming van hierdie klets kom saam.',
  'flowIdeas.memory.fresh': 'Begin ’n nuwe stel',
  'flowIdeas.history.title': 'Vroeëre voorstelle',
  'flowIdeas.history.fromKb': 'Uit jou kennisbasis',
  'flowIdeas.history.counts': '{{suggestions}} voorgestel, {{created}} gebou',
  'flowIdeas.fromInsights.intro': 'Uit my insigverslag het kliënte hieroor gevra en nie ’n antwoord gekry nie:',
  'flowIdeas.fromInsights.gap': '- {{theme}} ({{count}} gesprekke): {{why}}',
  'flowIdeas.fromInsights.document': '- ’n Dokument om by te voeg: {{title}}',
  'flowIdeas.error.credits': 'Daar is nie genoeg krediet hiervoor nie. Laai op onder Fakturering, of kies die standaardmodel.',
  'flowIdeas.error.busy': 'Die KI is nog besig met hierdie stel. Wag dat dit klaarmaak, of stop dit.',
  'flowIdeas.error.tooBig': 'Hierdie stel het te groot geword vir die KI om akkuraat te bly. Begin ’n nuwe stel om voort te gaan.',
  'flowIdeas.error.planLimit': 'Jou plan laat nie meer vloeie toe nie. Beginpunte tel nie. Verander jou plan onder Fakturering, of verwyder vloeie wat jy nie gebruik nie.',
  'flowIdeas.error.model': 'Daardie model is nie nou beskikbaar nie. Kies ’n ander in die instellings.',
  'flowIdeas.error.forbidden': 'Net die eienaar of ’n admin kan vloeie voorstel en bou.',
  'flowIdeas.chat.showAll': 'Wys alles ({{count}} karakters)',
  'flowIdeas.chat.showLess': 'Wys minder',
  'flowIdeas.activity.looseEnds': 'Gaan jou vloeie na vir los punte ({{count}} gevind)',
  'flowIdeas.replace.titleOne': '’n Verbeterde weergawe is gereed. Vervang die ou weergawe?',
  'flowIdeas.replace.title': '{{count}} verbeterde weergawes is gereed. Vervang die ou weergawes?',
  'flowIdeas.replace.what.links': 'Skakels in jou ander vloeie skuif na die nuwe weergawe.',
  'flowIdeas.replace.what.keys': 'Gestoorde sleutels en geskiedenis skuif ook.',
  'flowIdeas.replace.what.name': 'Die nuwe weergawe kry die ou naam en word aangeskakel.',
  'flowIdeas.replace.what.old': 'Die ou weergawe word geskrap, en jy kan dit 30 dae lank terugsit.',
  'flowIdeas.replace.goneOne': '{{old}} is reeds uitgevee. Vervang maak die werk klaar: skakels wat nog daarna wys, skuif na die nuwe weergawe.',
  'flowIdeas.replace.gone': 'Reeds uitgevee: {{names}}. Vervang maak die werk klaar: skakels wat nog daarna wys, skuif na die nuwe weergawes.',
  'flowIdeas.replace.all': 'Vervang alles',
  'flowIdeas.replace.allTitle': 'Vervang {{count}} ou weergawes?',
  'flowIdeas.replace.pair': '{{name}} vervang {{old}}',
  'flowIdeas.replace.allBody': 'Elke nuwe weergawe kry die ou naam en word aangeskakel, en die skakels in jou ander vloeie, gestoorde sleutels en geskiedenis skuif daarheen. Die ou weergawes word geskrap; jy kan elkeen 30 dae lank terugsit.',
  'flowIdeas.replace.one': 'Vervang',
  'flowIdeas.replace.oneTitle': 'Vervang {{old}}?',
  'flowIdeas.replace.oneBody': '{{name}} kry die naam {{old}} en word aangeskakel. Skakels in jou ander vloeie, gestoorde sleutels en geskiedenis skuif daarheen. Die ou {{old}} word geskrap, en jy kan dit 30 dae lank terugsit.',
  'flowIdeas.replace.oneBodyGone': '{{old}} is reeds uitgevee. Vervang maak die werk klaar: skakels wat nog daarna wys, skuif na {{name}}, wat die naam {{old}} kry en aangeskakel word.',
  'flowIdeas.replace.oldVersion': 'die ou weergawe',
  'flowIdeas.replace.replaced': 'Klaar vervang',
  'flowIdeas.replace.superseded': '’n Ander weergawe het die ou een reeds vervang.',
  'flowIdeas.replace.doneOne': 'Vervang. Die nuwe weergawe het die ou naam en is aangeskakel.',
  'flowIdeas.replace.doneAll': 'Ou weergawes vervang: {{count}}. Die nuwe weergawes het die ou name en is aangeskakel.',
  'flowIdeas.restore.button': 'Sit die ou weergawe terug',
  'flowIdeas.restore.title': 'Sit die ou weergawe terug?',
  'flowIdeas.restore.body': 'Die ou weergawe van {{old}} kom terug as ’n aparte vloei, afgeskakel, so kliënte merk niks nie. Skakels in jou vloeie bly by die nuwe weergawe.',
  'flowIdeas.restore.done': 'Die ou weergawe is terug, afgeskakel.',
  'flowIdeas.restore.open': 'Maak die ou weergawe oop',
  'flowIdeas.error.notImprovement': 'Daar is geen verbeterde weergawe oor om mee te vervang nie.',
  'flowIdeas.error.flowGone': 'Die verbeterde weergawe is uitgevee, so dit kan nie die ou een vervang nie.',
  'flowIdeas.error.nothingToRestore': 'Daar is geen ou weergawe om terug te sit nie.',
  'flowIdeas.error.alreadyRestored': 'Die ou weergawe is reeds terug.',
  'flowIdeas.error.tooLate': 'Ou weergawes word net 30 dae lank gehou.',
  'flowIdeas.error.planLimitRestore': 'Jou plan laat nie meer vloeie toe nie. Verander jou plan onder Fakturering, of verwyder ’n vloei wat jy nie gebruik nie.'
};

export default flowIdeas;
